import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Camera, Info, RotateCcw, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ImageConsentModal from '../../components/ImageConsentModal';
import UpgradeAccountModal from '../../components/UpgradeAccountModal';
import { useAuth } from '../../hooks/useAuth';
import { authedFetch } from '../../utils/authedRequest';
import { getColourAnalysisRequestUrl } from '../../config';
import { initAnalytics, submitColourAnalysisFeedback, submitColourAnalysisRun } from '../../utils/analytics';
import { preparePhotoForAnalysis } from './preparePhoto';
import { getColourAnalysisToken } from './colourAnalysisSession';
import { seasonMeaning } from './seasonPalette';
import { mentionsShade, personalPalette, reportColourExperience } from './personalPalette';
import { saveColourReport, latestColourReport, listColourReports, signedColourReportPhotos, updateColourReportExperience } from './colourReportStorage';
import './ColourAnalysisExperience.css';

const MAX_IMAGE_BYTES = 20_000_000;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
function createRunId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
const AXES = [
  ['temperature', 'Colour warmth', 'Cool', 'Warm'],
  ['overall_depth', 'Colour depth', 'Light', 'Deep'],
  ['colour_intensity', 'Colour intensity', 'Soft', 'Vivid'],
  ['contrast', 'Outfit contrast', 'Low', 'High'],
];
const PALETTE_GROUPS = [
  ['everyday_neutrals', 'Everyday neutrals'],
  ['reds_pinks_warmth', 'Reds, pinks & warmth'],
  ['greens_blues_purples', 'Greens, blues & purples'],
];
const TEMPERATURE_PREFERENCES = [
  ['warm', 'Warmer'], ['cool', 'Cooler'], ['both', 'Both'], ['unsure', 'Not sure'],
];
const ADJACENT_SEASONS = new Set([
  'Soft Autumn|Soft Summer', 'Light Spring|Light Summer',
  'Deep Autumn|Deep Winter', 'Bright Spring|Bright Winter',
]);

function TemperaturePreference({ value, onChange }) {
  return <fieldset className="ca-preference">
    <legend>When warm and cool colours both work, which do you prefer wearing?</legend>
    <div className="ca-preference-options">{TEMPERATURE_PREFERENCES.map(([option, label]) =>
      <button key={option} type="button" aria-pressed={(value || 'unsure') === option}
        onClick={() => onChange(option)}>{label}</button>
    )}</div>
    <p>This is your preference. It does not decide your colour season or foundation shade.</p>
  </fieldset>;
}

function closeColourDirection(analysis) {
  const { closest, neighbour } = analysis?.seasonal_interpretation || {};
  const pair = [closest, neighbour].sort().join('|');
  const warmth = Number(analysis?.dimensions?.temperature?.score);
  return ADJACENT_SEASONS.has(pair) && Number.isFinite(warmth)
    && warmth >= 0.33 && warmth <= 0.67 ? neighbour : null;
}

function relationshipTags(analysis) {
  const intensity = Number(analysis?.dimensions?.colour_intensity?.score);
  const warmth = Number(analysis?.dimensions?.temperature?.score);
  const tags = [];
  if (Number.isFinite(intensity)) {
    if (intensity <= 0.4) tags.push('Softer colours');
    else if (intensity >= 0.6) tags.push('Clearer colours');
  }
  if (Number.isFinite(warmth)) {
    if (warmth >= 0.33 && warmth <= 0.67) tags.push('Near-neutral warmth');
    else tags.push(warmth < 0.33 ? 'Cooler leaning' : 'Warmer leaning');
  }
  return tags;
}
const EXAMPLE_ANALYSIS = {
  suitability: { level: 'pass', usable: true },
  seasonal_interpretation: {
    closest: 'Soft Autumn', neighbour: 'Soft Summer',
    explanation: 'In this illustrative profile, gentle, slightly warm colours are a useful starting point.',
  },
  dimensions: {
    temperature: { label: 'Neutral warm', score: 0.58, what_it_means: 'Try balanced colours with a little warmth near the face.' },
    overall_depth: { label: 'Medium light', score: 0.38, what_it_means: 'Try lighter to medium colours before very dark ones.' },
    colour_intensity: { label: 'Soft', score: 0.35, what_it_means: 'Gently muted colours may feel easier to wear than very vivid ones.' },
    contrast: { label: 'Medium', score: 0.5, what_it_means: 'Medium contrast combinations are a useful place to start.' },
  },
  palette_direction: {
    everyday_neutrals: ['Soft ivory', 'Camel', 'Taupe', 'Chocolate brown'],
    reds_pinks_warmth: ['Soft coral', 'Peach', 'Terracotta', 'Muted oxblood'],
    greens_blues_purples: ['Sage green', 'Olive', 'Teal', 'Soft denim'],
  },
};

function Result({ analysis, onPalette, onRetake, onHistory, exampleMode, photoCount, temperaturePreference }) {
  const season = analysis.seasonal_interpretation;
  const caution = analysis.suitability.level === 'caution';
  const closeDirection = closeColourDirection(analysis);
  const tags = relationshipTags(analysis);
  const preferenceText = temperaturePreference === 'both'
    ? 'You enjoy both warm and cool colours.'
    : temperaturePreference === 'warm' ? 'You prefer warmer colours when both work.'
      : temperaturePreference === 'cool' ? 'You prefer cooler colours when both work.' : null;
  return (
    <div className="ca-result">
      {onHistory && <button className="ca-history-entry" type="button" onClick={onHistory}>
        <span><strong>Previous readings</strong><small>Revisit your saved photos and palettes</small></span>
        <ArrowRight size={18} aria-hidden="true" />
      </button>}
      <p className="ca-kicker">{exampleMode ? 'Example result' : 'Your colour starting point'}</p>
      <h1>{exampleMode ? `Example: ${season.closest}` : season.closest}</h1>
      {tags.length > 0 && <div className="ca-relationship-tags" aria-label="Your colour direction">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
      {seasonMeaning(season.closest) && <p className="ca-season-meaning">{seasonMeaning(season.closest)}</p>}
      {closeDirection && <p className="ca-close-direction">Close colour direction: <strong>{closeDirection}</strong></p>}
      {!exampleMode && preferenceText && <p className="ca-colour-preference">{preferenceText}</p>}
      {exampleMode && <p className="ca-v1-notice">Illustrative only. Your own result comes from your photo.</p>}
      {caution && <div className="ca-v1-notice"><Info size={17} /><p>{analysis.suitability.reasons?.[0] || 'Some details in your photos may affect this reading.'} Photos in even light may give a clearer result.</p></div>}
      <section className="ca-spectrum-section" aria-label="Your colour relationships">
        <h2>What this means for your colours</h2>
        <div className="ca-spectrum-grid">{AXES.map(([key, title, low, high]) => {
          const item = analysis.dimensions?.[key];
          if (!item) return null;
          const position = Math.min(100, Math.max(0, Number(item.score) * 100 || 0));
          return <div className={`ca-spectrum ca-spectrum--${key}`} key={key}>
            <div className="ca-spectrum-heading"><strong>{title}</strong><span>{item.label}</span></div>
            <div className="ca-spectrum-track" role="img" aria-label={`${title}: ${item.label}, from ${low} to ${high}`}><i style={{ left: `${position}%` }} /></div>
            <div className="ca-spectrum-ends"><span>{low}</span><span>{high}</span></div>
            <p>{item.what_it_means}</p>
          </div>;
        })}</div>
      </section>
      <div className="ca-actions ca-result-actions">
        <Button className="ca-primary" onClick={onPalette}>{exampleMode ? 'See example palette' : 'Explore my colours'} <ArrowRight size={17} /></Button>
        <button className="ca-text-action" type="button" onClick={onRetake}><RotateCcw size={14} /> {exampleMode ? 'Try with my photo' : 'Try another photo'}</button>
      </div>
      {!exampleMode && photoCount > 1 && <p className="ca-photo-variability">Different photos can shift this starting point, especially when light or makeup changes.</p>}
      <details className="ca-result-explanation">
        <summary>How we reached this result</summary>
        <p>We compared the same set of colours with {photoCount > 1 && !exampleMode ? 'each of your photos' : 'your photo'} and considered the colours you told us work for you when the comparison was close.</p>
      </details>
    </div>
  );
}

const FEEDBACK_CHOICES = [
  ['yes', 'Yes, mostly'], ['incomplete', 'Mostly, but incomplete'],
  ['partly', 'Some parts feel off'], ['no', 'Not really'],
];
const FEEDBACK_CONCERNS = [
  ['season', 'Season'], ['temperature', 'Warm or cool'], ['overall_depth', 'Light or deep'],
  ['colour_intensity', 'Soft or clear'], ['contrast', 'Contrast'],
  ['palette_missing', 'Colours I wear are missing'], ['palette', 'Suggested colours feel off'],
];

function Palette({ analysis, onNext, onRevise, onHistory, onBackToHistory, historical, nextHref, appOrigin, feedback, feedbackSaving, feedbackError, onFeedback, exampleMode, colourExperience, revisionChange }) {
  const palette = personalPalette(analysis, colourExperience);
  const knownGood = colourExperience?.knownGood?.trim();
  return <div className="ca-palette">
    <p className="ca-kicker">{exampleMode ? 'Example palette' : 'Your starting palette'}</p>
    <h1>Your colours.</h1>
    <p className="ca-subhead">{exampleMode ? 'A preview of your visual colour palette.' : 'A starting palette from your photos and the colours you shared.'}</p>
    <div className="ca-palette-families">
      {palette.groups.map(({ family, shades }) => <section className="ca-family" key={family}>
        <div className="ca-family-heading"><h2>{PALETTE_GROUPS.find(([key]) => key === family)?.[1]}</h2></div>
        <div className="ca-swatches">
          {shades.map(({ name, hex }) => <div className="ca-swatch" key={name}>
            <span style={{ background: hex }} aria-hidden="true" /><small>{name}</small>
          </div>)}
        </div>
      </section>)}
    </div>
    {!historical && <button className="ca-revise-link" type="button" onClick={onRevise}>Change colours that do or don’t suit me <ArrowRight size={16} /></button>}
    {(knownGood || palette.rejected.length > 0 || revisionChange) && <div className="ca-palette-notes" aria-label="How your answers shaped this palette">
      {knownGood && <p><strong>You said these work:</strong> {knownGood}</p>}
      {palette.rejected.length > 0 && <p><strong>Left out:</strong> {palette.rejected.join(', ')}</p>}
      {revisionChange && <p role="status"><strong>{revisionChange}</strong></p>}
    </div>}
    {palette.season && <>
      <section className="ca-reference-section" aria-label="Wider season colour palette">
        <h2 className="ca-palette-section-title">More colours to try</h2>
        <p className="ca-palette-section-intro">More from the season guide and any colours you told us work. These shades were not checked one by one against your photos.</p>
        <div className="ca-swatches ca-swatches--reference">{palette.more.map(({ name, hex, fromExperience }) => <div className="ca-swatch" key={name}><span style={{ background: hex }} aria-hidden="true" /><small>{name}{fromExperience ? ' · you said this works' : ''}</small></div>)}</div>
      </section>
      <section className="ca-reference-section ca-reference-section--caution" aria-label="Colours to approach with care">
        <h2 className="ca-palette-section-title">Colours to approach with care</h2>
        <p className="ca-palette-section-intro">From a general season guide. Your own experience comes first.</p>
        <div className="ca-swatches ca-swatches--reference">{palette.care.map(({ key, name, hex }) => <div className="ca-swatch" key={key}><span style={{ background: hex }} aria-hidden="true" /><small>{name}</small></div>)}</div>
      </section>
    </>}
    <p className="ca-palette-footnote">Screen shades are approximate. What works for you in person matters most.</p>
    {!exampleMode && !historical && <section className="ca-feedback" aria-label="Colour analysis feedback">
      {feedback?.submitted ? <><h2>Thank you for helping us improve.</h2><p>Your feedback was saved.</p></> : <>
        <h2>Does this result feel right to you?</h2>
        <p>Your view helps us test both the direction and whether these examples cover colours you actually wear.</p>
        <div className="ca-feedback-options">{FEEDBACK_CHOICES.map(([value, label]) =>
          <button key={value} type="button" aria-pressed={feedback?.verdict === value} onClick={() => onFeedback({ ...feedback, verdict: value, concern: null })}>{label}</button>
        )}</div>
        {feedback?.verdict && <>
          <p>{feedback.verdict === 'yes' ? 'Thank you. Add a note if you like.' : feedback.verdict === 'incomplete' ? 'What feels missing? You can skip this.' : 'Which part feels most off? You can skip this.'}</p>
          {feedback.verdict !== 'yes' && <div className="ca-feedback-options">{FEEDBACK_CONCERNS.map(([value, label]) =>
            <button key={value} type="button" aria-pressed={feedback.concern === value} onClick={() => onFeedback({ ...feedback, concern: value })}>{label}</button>
          )}</div>}
          <label className="ca-feedback-note" htmlFor="ca-feedback-note">What colours would you add, or what would you change? <span>(optional)</span></label>
          <textarea id="ca-feedback-note" className="ca-feedback-textarea" maxLength={500} rows={4}
            placeholder="For example, I like these, but deep red also works well for me."
            value={feedback.note || ''} onChange={(event) => onFeedback({ ...feedback, note: event.target.value })} />
          <p className="ca-feedback-privacy">Your note is saved with your feedback. {String(feedback.note || '').length}/500</p>
          {feedbackError && <p className="ca-v1-error" role="alert">{feedbackError}</p>}
          <Button className="ca-primary" disabled={feedbackSaving} onClick={() => onFeedback({ ...feedback, submitted: true })}>{feedbackSaving ? 'Saving…' : 'Send feedback'}</Button>
        </>}
      </>}
    </section>}
    <div className="ca-actions">
      {historical ? <button className="ca-text-action" type="button" onClick={onBackToHistory}>Back to previous readings</button> : <>
      {nextHref ? <a className="ca-primary ca-primary-link" href={nextHref}>Continue with Son:mi <ArrowRight size={17} /></a>
        : <Button className="ca-primary" onClick={onNext}>{exampleMode ? 'Try with my photos' : 'Save my colours to continue'} <ArrowRight size={17} /></Button>}
      {!exampleMode && <a className="ca-text-action" href={appOrigin}>Go to chat</a>}
      {onHistory && <button className="ca-text-action" type="button" onClick={onHistory}>Previous readings</button>}
      </>}
    </div>
  </div>;
}

function PreviousReadings({ reports, photoUrls, loading, error, onOpen, onCurrent, onNew }) {
  const previous = reports.slice(1);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? previous : previous.slice(0, 1);
  return <div className="ca-history">
    <p className="ca-kicker">My colours</p>
    <h1>Previous readings.</h1>
    <p className="ca-subhead">Photos and improvements to our analysis can change a reading. Son:mi uses your newest result for advice.</p>
    {loading && <p role="status">Opening your readings…</p>}
    {error && <p className="ca-v1-error" role="alert">{error}</p>}
    {!loading && !error && <>
      {reports[0] && <button className="ca-history-current" type="button" onClick={onCurrent}>Current reading: {reports[0].analysis?.seasonal_interpretation?.closest || 'Your colours'} <ArrowRight size={17} /></button>}
      {!reports.length && <div className="ca-history-empty"><p>No readings saved yet. Start with your photos.</p><Button className="ca-primary" onClick={onNew}>Get started <ArrowRight size={17} /></Button></div>}
      {previous.length ? <div className="ca-history-list">{visible.map((report) => <button className="ca-history-item" type="button" key={report.id} onClick={() => onOpen(report)}>
        {photoUrls[report.id]?.[0] ? <img src={photoUrls[report.id][0]} alt="" /> : <span className="ca-history-photo-fallback" aria-hidden="true" />}
        <span className="ca-history-item-copy"><strong>{new Date(report.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</strong><small>{report.analysis?.seasonal_interpretation?.closest || 'Colour reading'}</small></span>
        <ArrowRight size={17} aria-hidden="true" />
      </button>)}{previous.length > 1 && !showAll && <button className="ca-history-more" type="button" onClick={() => setShowAll(true)}>Show all {previous.length} earlier readings</button>}</div> : reports.length > 0 && <p className="ca-history-empty">Your earlier readings will appear here after you save another result.</p>}
    </>}
  </div>;
}

function toggleNamedShade(answer, name) {
  const pieces = String(answer || '').split(/[,;\n]|\s+and\s+/i).map((piece) => piece.trim()).filter(Boolean);
  const matching = pieces.find((piece) => piece.toLowerCase() === name.toLowerCase());
  return matching ? pieces.filter((piece) => piece !== matching).join(', ')
    : [...pieces, name].join(', ').slice(0, 160);
}

function RevisePalette({ analysis, initial, onChange, onSave, onCancel, onNewReading, saving, error }) {
  const draftPalette = personalPalette(analysis, initial);
  const choices = [...draftPalette.groups.flatMap(({ shades }) => shades), ...draftPalette.more];
  return <div className="ca-questions ca-revise">
    <p className="ca-kicker">Fine-tune your colours</p>
    <h1>What would you change?</h1>
    <p className="ca-subhead">Tap a shade that does not suit you, or name one below. We will update your palette from the fixed colour catalogue. Your photo reading and season will stay the same.</p>
    <div className="ca-revise-grid" aria-label="Tap shades to leave out">
      {choices.map(({ name, hex }) => <button type="button" key={name}
        className="ca-revise-swatch" aria-pressed={mentionsShade(initial.knownBad, name)}
        onClick={() => onChange({ ...initial, knownBad: toggleNamedShade(initial.knownBad, name) })}>
        <span className="ca-revise-swatch-colour" style={{ background: hex }} aria-hidden="true" />
        <span>{name}</span>
        {mentionsShade(initial.knownBad, name) && <strong>Leave out</strong>}
      </button>)}
    </div>
    <label htmlFor="ca-revise-bad">Other colours that do not look good on you</label>
    <input id="ca-revise-bad" maxLength={160} value={initial.knownBad}
      placeholder="For example, fuchsia and hot pink"
      onChange={(event) => onChange({ ...initial, knownBad: event.target.value })} />
    <label htmlFor="ca-revise-good">Colours you know work well</label>
    <input id="ca-revise-good" maxLength={160} value={initial.knownGood}
      placeholder="For example, burgundy and espresso"
      onChange={(event) => onChange({ ...initial, knownGood: event.target.value })} />
    <label htmlFor="ca-revise-favourites">Colours you love wearing</label>
    <input id="ca-revise-favourites" maxLength={160} value={initial.favourites}
      placeholder="For example, purple"
      onChange={(event) => onChange({ ...initial, favourites: event.target.value })} />
    <TemperaturePreference value={initial.temperaturePreference}
      onChange={(value) => onChange({ ...initial, temperaturePreference: value })} />
    {error && <p className="ca-v1-error" role="alert">{error}</p>}
    <div className="ca-actions">
      <Button className="ca-primary" disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Update my palette'} <ArrowRight size={17} /></Button>
      <button className="ca-text-action" type="button" onClick={onCancel}>Back to my colours</button>
      <button className="ca-text-action" type="button" onClick={onNewReading}>Start a new photo reading</button>
    </div>
  </div>;
}

function NextSteps({ savedReport, studyMode, onSaveAgain, savingReport, saveError, exampleMode }) {
  const appOrigin = studyMode ? 'https://my-app-three-murex.vercel.app' : window.location.origin;
  return <div className="ca-next">
    <p className="ca-kicker">Save your result</p>
    <h1>Keep your colours.</h1>
    <p className="ca-subhead">Save this report to your account before continuing.</p>
    {saveError && !exampleMode && <p className="ca-v1-error" role="alert">{saveError}</p>}
    {!savedReport && !exampleMode && <Button className="ca-primary" disabled={savingReport} onClick={onSaveAgain}>{savingReport ? 'Saving…' : 'Try saving my colours again'}</Button>}
    <a className="ca-text-action" href={appOrigin}>Go to chat</a>
    {savedReport && !exampleMode && <a className="ca-text-action" href={`${appOrigin}/?journey=colour-makeup&from=colour-analysis`}>Continue with Son:mi</a>}
  </div>;
}

export default function ColourAnalysisV1() {
  const studyMode = process.env.REACT_APP_COLOUR_STUDY_ONLY === 'true';
  const multiPhotoMode = true;
  const { user, loading, isAnonymous } = useAuth();
  const [screen, setScreen] = useState(new URLSearchParams(window.location.search).get('view') === 'saved' ? 'saved' : 'intro');
  const [analysis, setAnalysis] = useState(null);
  const [suitability, setSuitability] = useState(null);
  const [resultToken, setResultToken] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [additionalPhotos, setAdditionalPhotos] = useState([]);
  const [additionalPhotoUrls, setAdditionalPhotoUrls] = useState([]);
  const [source, setSource] = useState('upload');
  const [consentOpen, setConsentOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [exampleMode, setExampleMode] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [analysisRunId, setAnalysisRunId] = useState(null);
  const [savedReport, setSavedReport] = useState(null);
  const [historyReturn, setHistoryReturn] = useState('saved');
  const [historyReports, setHistoryReports] = useState([]);
  const [historyPhotos, setHistoryPhotos] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [savingReport, setSavingReport] = useState(false);
  const [studySaveError, setStudySaveError] = useState('');
  const [colourExperience, setColourExperience] = useState({ knownGood: '', knownBad: '', favourites: '', temperaturePreference: 'unsure' });
  const [revisionDraft, setRevisionDraft] = useState({ knownGood: '', knownBad: '', favourites: '', temperaturePreference: 'unsure' });
  const [revisionChange, setRevisionChange] = useState('');
  const [revisionSaving, setRevisionSaving] = useState(false);
  const [revisionError, setRevisionError] = useState('');
  const [authSlow, setAuthSlow] = useState(false);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);
  const additionalUploadRef = useRef(null);
  const extraUrlRef = useRef([]);
  const requestRef = useRef(null);
  const preparedPhotosRef = useRef([]);
  const runIdRef = useRef(null);

  useEffect(() => {
    setHistoryReports([]);
    setHistoryPhotos({});
    setSelectedHistoryReport(null);
    setScreen((previous) => previous.startsWith('history') ? 'intro' : previous);
  }, [user?.id]);

  useEffect(() => () => {
    requestRef.current?.abort();
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);
  useEffect(() => () => extraUrlRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  useEffect(() => {
    const page = document.querySelector('.ca-page');
    if (page) page.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [screen]);

  useEffect(() => {
    if (!loading) { setAuthSlow(false); return undefined; }
    const timer = window.setTimeout(() => setAuthSlow(true), 10000);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (user?.id) initAnalytics(user.id, isAnonymous);
  }, [user?.id, isAnonymous]);

  useEffect(() => {
    if (screen !== 'saved' || loading) return;
    if (isAnonymous) { setScreen('intro'); return; }
    latestColourReport().then((report) => {
      if (!report) { setScreen('intro'); return; }
      setSavedReport(report);
      setAnalysis(report.analysis);
      setColourExperience({
        knownGood: report.colour_experience?.knownGood || '',
        knownBad: report.colour_experience?.knownBad || '',
        favourites: report.colour_experience?.favourites || '',
        temperaturePreference: report.colour_experience?.temperaturePreference || 'unsure',
      });
    }).catch(() => { setError('We could not load your saved colours.'); setScreen('intro'); });
  }, [screen, loading, isAnonymous]);

  useEffect(() => {
    if (screen !== 'history' || loading) return undefined;
    if (isAnonymous) { setScreen('intro'); return undefined; }
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    listColourReports().then((reports) => {
      if (!active) return;
      setHistoryReports(reports);
      setHistoryLoading(false);
      reports.slice(1).forEach((report) => {
        signedColourReportPhotos({ ...report, photo_paths: report.photo_paths?.slice(0, 1) || [] })
          .then((urls) => { if (active) setHistoryPhotos((previous) => ({ ...previous, [report.id]: urls })); })
          .catch(() => {});
      });
    }).catch(() => {
      if (active) { setHistoryError('We could not open your previous readings. Please try again.'); setHistoryLoading(false); }
    });
    return () => { active = false; };
  }, [screen, loading, isAnonymous]);

  useEffect(() => {
    if (!selectedHistoryReport || (screen !== 'history-profile' && screen !== 'history-palette')) return undefined;
    let active = true;
    signedColourReportPhotos(selectedHistoryReport).then((urls) => {
      if (active) setHistoryPhotos((previous) => ({ ...previous, [selectedHistoryReport.id]: urls }));
    }).catch(() => {});
    return () => { active = false; };
  }, [screen, selectedHistoryReport]);

  const openHistory = () => { setHistoryReturn(screen); setSelectedHistoryReport(null); setScreen('history'); };

  const persistResult = async (result) => {
    if (!preparedPhotosRef.current.length || !runIdRef.current) return;
    setSavingReport(true);
    try {
      const report = await saveColourReport({
        runId: runIdRef.current, analysis: result,
        colourExperience: reportColourExperience(result, colourExperience), photoData: preparedPhotosRef.current,
      });
      setSavedReport({ ...report, analysis: result,
        colour_experience: reportColourExperience(result, colourExperience) });
      preparedPhotosRef.current = [];
      setStudySaveError('');
    } catch (_) {
      setStudySaveError('We could not save your photos and result to your account. Your result is still visible; please try saving again.');
    } finally { setSavingReport(false); }
  };

  const updateFeedback = async (next) => {
    if (!next.submitted) { setFeedback(next); setFeedbackError(''); return; }
    if (feedbackSaving || feedback?.submitted || !analysis) return;
    setFeedbackSaving(true);
    setFeedbackError('');
    try {
      const { error: saveError } = await submitColourAnalysisFeedback({
        verdict: next.verdict, concern: next.concern, note: next.note,
        season: analysis.seasonal_interpretation.closest,
        neighbour: analysis.seasonal_interpretation.neighbour,
        analysisVersion: analysis.analysis_version || 'unknown',
        runId: analysisRunId,
      });
      if (saveError) throw saveError;
      setFeedback(next);
    } catch (_) { setFeedbackError('Your answer could not be saved. Please try again.'); }
    finally { setFeedbackSaving(false); }
  };

  useEffect(() => {
    if (!isAnonymous && screen === 'gate' && analysis) setScreen('profile');
  }, [isAnonymous, screen, analysis]);

  useEffect(() => {
    if (screen === 'gate' && isAnonymous) setRegisterOpen(true);
  }, [screen, isAnonymous]);

  const choosePhoto = (nextSource) => { setSource(nextSource); setConsentOpen(true); };
  const showExample = () => {
    setConsentOpen(false);
    setExampleMode(true);
    setRevisionChange('');
    setAnalysis(EXAMPLE_ANALYSIS);
    setScreen('profile');
  };
  const afterConsent = () => {
    setConsentOpen(false);
    (source === 'camera' ? cameraRef : uploadRef).current?.click();
  };
  const onFile = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (files.length > (multiPhotoMode ? 3 : 1)) { setError('Choose up to three photos.'); return; }
    if (files.some((file) => !ACCEPTED_TYPES.has(file.type))) { setError('Choose JPEG, PNG or WebP photos.'); return; }
    if (files.some((file) => file.size > MAX_IMAGE_BYTES)) { setError('Choose photos smaller than 20 MB each.'); return; }
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    extraUrlRef.current.forEach((url) => URL.revokeObjectURL(url));
    const extraUrls = files.slice(1).map((file) => URL.createObjectURL(file));
    extraUrlRef.current = extraUrls;
    setPhoto(files[0]);
    setPhotoUrl(URL.createObjectURL(files[0]));
    setAdditionalPhotos(files.slice(1));
    setAdditionalPhotoUrls(extraUrls);
    setAnalysis(null);
    setExampleMode(false);
    setRevisionChange('');
    setSuitability(null);
    setResultToken(null);
    setGuestToken(null);
    setFeedback(null);
    setAnalysisRunId(null);
    preparedPhotosRef.current = [];
    runIdRef.current = null;
    setSavedReport(null);
    setStudySaveError('');
    setError('');
    setScreen('review');
  };
  const onAdditionalFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (files.length + additionalPhotos.length > 2) { setError('Use up to three photos in total.'); return; }
    if (files.some((file) => !ACCEPTED_TYPES.has(file.type) || file.size > MAX_IMAGE_BYTES)) { setError('Choose JPEG, PNG or WebP photos smaller than 20 MB each.'); return; }
    const urls = files.map((file) => URL.createObjectURL(file));
    extraUrlRef.current = [...extraUrlRef.current, ...urls];
    setAdditionalPhotos((previous) => [...previous, ...files]);
    setAdditionalPhotoUrls((previous) => [...previous, ...urls]);
    setError('');
  };
  const afterAnalyse = (photoDecision) => setScreen(photoDecision.level === 'caution' ? 'caution' : isAnonymous ? 'gate' : 'profile');
  const analyse = async () => {
    if (!photo || !user || (multiPhotoMode && additionalPhotos.length < 1)) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setError('');
    setStudySaveError('');
    setScreen('analysing');
    try {
      const accessToken = await getColourAnalysisToken(isAnonymous);
      if (isAnonymous) setGuestToken(accessToken);
      const photoData = await Promise.all([photo, ...(multiPhotoMode ? additionalPhotos : [])]
        .map((file) => preparePhotoForAnalysis(file, 1_250_000)));
      const [image_data, ...additional_images] = photoData;
      preparedPhotosRef.current = photoData;
      const response = await authedFetch(getColourAnalysisRequestUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          colourAnalysisV1: studyMode ? 'analyse_study' : 'analyse', photo_consent: true, image_data,
          ...(multiPhotoMode ? { additional_images, colour_experience: {
            known_good: colourExperience.knownGood.trim(), known_bad: colourExperience.knownBad.trim(),
            favourites: colourExperience.favourites.trim(),
            temperature_preference: colourExperience.temperaturePreference || 'unsure',
          } } : {}),
        }),
        signal: controller.signal,
      });
      const payload = await response.json();
      if (response.status === 401) throw new Error('Your session expired while analysing. Please reload this page and try again.');
      if (!response.ok || !payload.success) throw new Error('Colour analysis is unavailable right now. Please try again.');
      const runId = createRunId();
      setAnalysisRunId(runId);
      runIdRef.current = runId;
      setSuitability(payload.suitability);
      setResultToken(payload.result_token || null);
      setAnalysis(payload.analysis || null);
      if (!payload.suitability.usable) setScreen('retake');
      else {
        if (!isAnonymous && payload.analysis) await persistResult(payload.analysis);
        if (studyMode && payload.analysis) Promise.resolve(submitColourAnalysisRun({
          runId, analysis: payload.analysis, suitability: payload.suitability,
          colourExperience, photoCount: photoData.length,
        })).catch(() => {});
        afterAnalyse(payload.suitability);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err instanceof TypeError ? 'Could not reach the analysis service. Please check your connection and try again.' : err.message || 'Analysis failed. Please try again.');
        setScreen('review');
      }
    } finally { requestRef.current = null; }
  };
  const revealResult = async () => {
    setRegisterOpen(false);
    if (analysis) { setScreen('profile'); return; }
    if (!resultToken) { setError('Your result is no longer available. Please analyse your photo again.'); setScreen('review'); return; }
    setScreen('revealing');
    try {
      const response = await authedFetch(getColourAnalysisRequestUrl(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colourAnalysisV1: 'reveal', result_token: resultToken, guest_token: guestToken }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error('Your result could not be opened. Please try again.');
      setAnalysis(payload.analysis);
      await persistResult(payload.analysis);
      if (studyMode) Promise.resolve(submitColourAnalysisRun({
        runId: runIdRef.current, analysis: payload.analysis, suitability,
        colourExperience, photoCount: 1 + additionalPhotos.length,
      })).catch(() => {});
      setResultToken(null);
      setGuestToken(null);
      setScreen('profile');
    } catch (err) { setError(err.message); setScreen('gate'); }
  };
  const retake = () => { requestRef.current?.abort(); preparedPhotosRef.current = []; runIdRef.current = null; setAnalysis(null); setExampleMode(false); setSuitability(null); setResultToken(null); setGuestToken(null); setFeedback(null); setRevisionChange(''); setScreen('questions'); };
  const openRevision = () => { setRevisionError(''); setRevisionDraft({ ...colourExperience }); setScreen('revise'); };
  const saveRevision = async () => {
    if (revisionSaving) return;
    const before = personalPalette(analysis, colourExperience);
    const after = personalPalette(analysis, revisionDraft);
    const beforeNames = [...before.groups.flatMap(({ shades }) => shades), ...before.more].map(({ name }) => name);
    const afterNames = [...after.groups.flatMap(({ shades }) => shades), ...after.more].map(({ name }) => name);
    const removed = beforeNames.filter((name) => !afterNames.includes(name));
    const added = afterNames.filter((name) => !beforeNames.includes(name));
    const canPersistRevision = !exampleMode && Boolean(savedReport?.id);
    if (canPersistRevision) {
      setRevisionSaving(true);
      setRevisionError('');
      try {
        const stored = await updateColourReportExperience(savedReport.id,
          reportColourExperience(analysis, revisionDraft));
        setSavedReport((previous) => ({ ...previous, colour_experience: stored }));
      } catch (_) {
        setRevisionError('Your changes could not be saved. Please try again.');
        setRevisionSaving(false);
        return;
      }
      setRevisionSaving(false);
    }
    setColourExperience({ ...revisionDraft });
    setRevisionChange(removed.length || added.length
      ? canPersistRevision ? 'Palette updated and saved.' : 'Palette updated. Save this report to keep your changes.'
      : canPersistRevision ? 'Answers saved; suggested shades stayed the same.' : 'Answers updated for this preview.');
    setScreen('palette');
  };
  const goBack = () => {
    if (exampleMode && (screen === 'profile' || screen === 'palette')) { setExampleMode(false); setAnalysis(null); setScreen('intro'); }
    else if (screen === 'history-palette') setScreen('history-profile');
    else if (screen === 'history-profile') setScreen('history');
    else if (screen === 'history') setScreen(historyReturn);
    else if (screen === 'palette') setScreen('profile');
    else if (screen === 'revise') setScreen('palette');
    else if (screen === 'next') setScreen('palette');
    else if (screen === 'saved') window.location.assign('/');
    else if (screen === 'profile' || screen === 'gate' || screen === 'caution' || screen === 'retake') retake();
    else if (screen === 'review' || screen === 'analysing') { requestRef.current?.abort(); setScreen('prepare'); }
    else if (screen === 'prepare') setScreen('questions');
    else if (screen === 'questions') setScreen('intro');
    else window.location.assign('/');
  };

  if (loading || !user) return <div className="ca-page"><main className="ca-main ca-session-start">
    {loading && !authSlow ? <p role="status">Preparing Son:mi…</p> : <>
      <h1>We couldn’t open your analysis.</h1>
      <p className="ca-subhead">Please check your connection and try again.</p>
      <Button className="ca-primary" onClick={() => window.location.reload()}>Try again</Button>
    </>}
  </main></div>;
  return <>
    <div className="ca-page">
      <header className="ca-header"><button className="ca-back" type="button" onClick={goBack} aria-label="Go back"><ArrowLeft size={18} /></button><span className="ca-header-title">Colour analysis</span><span /></header>
      <main className="ca-main">
        <input className="ca-file-input" ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" onChange={onFile} />
        <input className="ca-file-input" ref={uploadRef} type="file" accept="image/jpeg,image/png,image/webp" multiple={multiPhotoMode} onChange={onFile} />
        {multiPhotoMode && <input className="ca-file-input" ref={additionalUploadRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onAdditionalFiles} />}
        {screen === 'intro' && <div className="ca-intro"><p className="ca-kicker">A starting point for colour</p><h1>Find the colours that feel like you.</h1><p className="ca-subhead">Son:mi analyses your photos and explains the colour relationships that may work near your face. Create a free account to see your colour season and palette, and return to them later.</p><div className="ca-actions"><Button className="ca-primary" onClick={() => setScreen('questions')}>Get started <ArrowRight size={17} /></Button>{!isAnonymous && <Button variant="outline" className="ca-secondary" onClick={openHistory}>My readings</Button>}</div></div>}
        {screen === 'questions' && <div className="ca-questions"><p className="ca-kicker">Before your photos</p><h1>What colours do you already know?</h1><p className="ca-subhead">Tell us what works and what doesn't. We use this when photo comparisons are close and leave named poor matches out of your palette.</p><label htmlFor="ca-known-good">Which colours near your face have reliably looked good or drawn compliments?</label><input id="ca-known-good" maxLength={160} value={colourExperience.knownGood} placeholder="For example, burgundy and espresso brown" onChange={(event) => setColourExperience((previous) => ({ ...previous, knownGood: event.target.value }))} /><label htmlFor="ca-known-bad">Which colours do you think do not look good on you?</label><input id="ca-known-bad" maxLength={160} value={colourExperience.knownBad} placeholder="For example, light grey or yellowish beige" onChange={(event) => setColourExperience((previous) => ({ ...previous, knownBad: event.target.value }))} /><label htmlFor="ca-favourites">Which colours do you simply love wearing?</label><input id="ca-favourites" maxLength={160} value={colourExperience.favourites} placeholder="For example, purple, even if it is not your best match" onChange={(event) => setColourExperience((previous) => ({ ...previous, favourites: event.target.value }))} /><TemperaturePreference value={colourExperience.temperaturePreference} onChange={(value) => setColourExperience((previous) => ({ ...previous, temperaturePreference: value }))} /><div className="ca-actions"><Button className="ca-primary" onClick={() => setScreen('prepare')}>Continue to photos <ArrowRight size={17} /></Button></div></div>}
        {screen === 'prepare' && <div className="ca-prepare"><p className="ca-kicker">Before your photos</p><h1>Give us a clear view.</h1><p className="ca-subhead">Choose two or three photos. Make the first front-facing, with your face clearly visible. Add another in similar, even light. Avoid filters, strong colour makeup and heavy shadows. Different outfits are fine.</p>{error && <p className="ca-v1-error" role="alert">{error}</p>}<div className="ca-actions"><Button className="ca-primary" onClick={() => choosePhoto('camera')}><Camera size={17} /> Take a photo</Button><Button variant="outline" className="ca-secondary" onClick={() => choosePhoto('upload')}><Upload size={17} /> Upload photos</Button></div></div>}
        {screen === 'review' && <div className="ca-photo-review"><p className="ca-kicker">Your photos</p><h1>Ready to analyse?</h1><p className="ca-subhead">{`${1 + additionalPhotos.length} of 2–3 photos added. ${additionalPhotos.length ? 'We will compare the same colours with each photo, then look for what stays consistent.' : 'Add one more photo of yourself to continue.'}`}</p><div className="ca-photo-preview">{photoUrl && <img src={photoUrl} alt="Primary view selected for colour analysis" />}</div>{multiPhotoMode && <><div className="ca-extra-photos">{additionalPhotoUrls.map((url, index) => <img key={url} src={url} alt={`Additional view ${index + 2}`} />)}</div>{additionalPhotos.length < 2 && <button className="ca-text-action" type="button" onClick={() => additionalUploadRef.current?.click()}>Add another photo</button>}</>}{error && <p className="ca-v1-error" role="alert">{error}</p>}<div className="ca-actions"><Button className="ca-primary" disabled={multiPhotoMode && !additionalPhotos.length} onClick={analyse}>Analyse my photos <ArrowRight size={17} /></Button><button className="ca-text-action" type="button" onClick={() => choosePhoto('upload')}>Choose different photos</button></div></div>}
        {screen === 'analysing' && <div className="ca-analysing" role="status" aria-live="polite"><div className="ca-loading-swatches" aria-hidden="true"><i /><i /><i /><i /><i /></div><p className="ca-kicker">Analysing your photos</p><h1>Finding your colour direction.</h1><p className="ca-subhead">Comparing colours across your photos. This can take a minute; please keep this page open.</p></div>}
        {screen === 'retake' && <div><p className="ca-kicker">A clearer photo will help</p><h1>Let's try again.</h1><p className="ca-subhead">{suitability?.guidance}</p><ul className="ca-v1-reasons">{suitability?.reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul><div className="ca-actions"><Button className="ca-primary" onClick={retake}>Choose another photo</Button></div></div>}
        {screen === 'caution' && <div><p className="ca-kicker">Photo check</p><h1>We can continue, with a caveat.</h1><p className="ca-subhead">Your photo is readable, but these details could affect the result:</p><ul className="ca-v1-reasons">{suitability?.reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul>{studySaveError && <p className="ca-v1-error" role="alert">{studySaveError}</p>}<div className="ca-actions"><Button className="ca-primary" onClick={() => setScreen(isAnonymous ? 'gate' : 'profile')}>Continue with this photo <ArrowRight size={17} /></Button><button className="ca-text-action" type="button" onClick={retake}>Use another photo</button></div></div>}
        {screen === 'gate' && <div className="ca-v1-gate"><p className="ca-kicker">Your analysis is ready</p><h1>See your colours.</h1><p className="ca-subhead">Create a free account or sign in to see your result and personal palette.</p>{error && <p className="ca-v1-error" role="alert">{error}</p>}<div className="ca-actions"><Button className="ca-primary" onClick={() => isAnonymous ? setRegisterOpen(true) : revealResult()}>{isAnonymous ? 'Create account or sign in' : 'Open my result'} <ArrowRight size={17} /></Button></div></div>}
        {screen === 'revealing' && <div className="ca-analysing ca-revealing" role="status" aria-live="polite"><div className="ca-loading-swatches" aria-hidden="true"><i /><i /><i /><i /><i /></div><p className="ca-kicker">Opening your profile</p><h1>Almost there.</h1><p className="ca-subhead">Bringing your colours together.</p></div>}
        {screen === 'saved' && !analysis && <div className="ca-analysing"><p className="ca-kicker">My colours</p><h1>Opening your saved report.</h1></div>}
        {screen === 'saved' && analysis && <Result analysis={analysis} onPalette={() => setScreen('palette')} onRetake={retake} onHistory={openHistory} exampleMode={false} photoCount={savedReport?.photo_paths?.length || 1} temperaturePreference={colourExperience.temperaturePreference} />}
        {screen === 'profile' && analysis && <>{studySaveError && <p className="ca-v1-error" role="alert">{studySaveError}</p>}<Result analysis={analysis} onPalette={() => setScreen('palette')} onRetake={retake} onHistory={!exampleMode && savedReport ? openHistory : null} exampleMode={exampleMode} photoCount={1 + additionalPhotos.length} temperaturePreference={colourExperience.temperaturePreference} /></>}
        {screen === 'palette' && analysis && <Palette analysis={analysis} onNext={() => setScreen(exampleMode ? 'intro' : 'next')} onRevise={openRevision} onHistory={!exampleMode && savedReport ? openHistory : null} nextHref={!exampleMode && savedReport ? `${studyMode ? 'https://my-app-three-murex.vercel.app' : window.location.origin}/?journey=colour-makeup&from=colour-analysis` : null} appOrigin={studyMode ? 'https://my-app-three-murex.vercel.app' : window.location.origin} feedback={feedback} feedbackSaving={feedbackSaving} feedbackError={feedbackError} onFeedback={updateFeedback} exampleMode={exampleMode} colourExperience={colourExperience} revisionChange={revisionChange} />}
        {screen === 'history' && <PreviousReadings reports={historyReports} photoUrls={historyPhotos} loading={historyLoading} error={historyError} onOpen={(report) => { setSelectedHistoryReport(report); setScreen('history-profile'); }} onCurrent={() => { setSelectedHistoryReport(null); setScreen('saved'); }} onNew={() => setScreen('questions')} />}
        {screen === 'history-profile' && selectedHistoryReport && <div className="ca-history-detail">
          <p className="ca-history-date">Saved {new Date(selectedHistoryReport.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          {historyPhotos[selectedHistoryReport.id]?.some(Boolean) && <section className="ca-history-photos" aria-label="Photos saved with this reading">
            <h2>Your photos</h2>
            <div>{historyPhotos[selectedHistoryReport.id].map((url, index) => url && <img key={index} src={url} alt={`Saved view ${index + 1} for this reading`} />)}</div>
          </section>}
          <Result analysis={selectedHistoryReport.analysis} onPalette={() => setScreen('history-palette')} onRetake={retake} exampleMode={false} photoCount={selectedHistoryReport.photo_paths?.length || 1} temperaturePreference={selectedHistoryReport.colour_experience?.temperaturePreference} />
        </div>}
        {screen === 'history-palette' && selectedHistoryReport && <Palette analysis={selectedHistoryReport.analysis} historical onBackToHistory={() => setScreen('history')} appOrigin={studyMode ? 'https://my-app-three-murex.vercel.app' : window.location.origin} exampleMode={false} colourExperience={selectedHistoryReport.colour_experience || {}} />}
        {screen === 'revise' && analysis && <RevisePalette analysis={analysis} initial={revisionDraft} onChange={setRevisionDraft} onSave={saveRevision} onCancel={() => setScreen('palette')} onNewReading={retake} saving={revisionSaving} error={revisionError} />}
        {screen === 'next' && analysis && <NextSteps savedReport={savedReport} studyMode={studyMode} onSaveAgain={() => persistResult(analysis)} savingReport={savingReport} saveError={studySaveError} exampleMode={exampleMode} />}
      </main>
    </div>
    <ImageConsentModal isOpen={consentOpen} context={studyMode ? 'colour-analysis-public-study' : 'colour-analysis'} onAllow={afterConsent} onNotNow={() => setConsentOpen(false)} />
    <UpgradeAccountModal skipPitch simpleForm isOpen={registerOpen} onClose={() => setRegisterOpen(false)} onSuccess={revealResult} />
  </>;
}
